import map from 'lodash/map';
import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import values from 'lodash/values';
import uniq from 'lodash/uniq';

import { NGI_WIDGETS_IN_DESIGNER_FF_TOKEN } from '@lumapps/widget-base/constants';
import { fetchCommunityMetadata } from '@lumapps/widget-community-list/api/fetchCommunityMetadata';
import { createMetadataMap } from '@lumapps/widget-community-list/utils/createMetadataMap';

(function IIFE() {
    'use strict';

    /////////////////////////////

    function WidgetCommunityListController($scope, Community, Config, Content, ContentPicker, ContentTemplate, Instance,
        Utils, Features) {
        'ngInject';

        var vm = this;

        /////////////////////////////
        //                         //
        //    Private attributes   //
        //                         //
        /////////////////////////////

        /**
         * Community picker modal identifier - only for basic mode.
         *
         * @type {string}
         */
        var _COMMUNITY_PICKER_ID = 'widget-community-list-settings-content-picker-';

        /**
         * Describe the fields we want to get in the list.
         *
         * @type {Object}
         */
        var _projection = {};

        /**
         * Describe the fields we want to get in the `subscription` field if needed.
         *
         * @type {Object}
         */
        var _subscriptionProjection = {
            customerKey: true,
            followerUserKey: true,
            followingContentKey: true,
            followingContentType: true,
            id: true,
            instanceKey: true,
            notify: true,
            type: true,
            uid: true,
        };

        /**
         * Map of metadata ID to Metadata object for efficient lookups
         *
         * @type {Map<string, Metadata>}
         */
        vm.metadataMap = new Map();

        /**
         * Whether the widget should be displayed in NGI or not.
         */
        vm.isNGICompatible = Utils.isDesignerMode() && Features.hasFeature(NGI_WIDGETS_IN_DESIGNER_FF_TOKEN) && Features.hasFeature('layout-v2');

        /////////////////////////////
        //                         //
        //    Private functions    //
        //                         //
        /////////////////////////////

        function _shouldDisplayAsNGI() {
            return vm.isNGICompatible;
        }

        function _setAsNonNgiCompatible() {
            const isNGICompatiblePreviousStatus = vm.isNGICompatible;
            vm.isNGICompatible = false;

            // Set the widget as non NGI compatible will fallback to the
            // legacy display. So we need to re-init the content list.
            if (isNGICompatiblePreviousStatus) {
                _initList();
            }
        }

        function _setAsNgiCompatible() {
            const isInDesignerContext = vm.widgetListCtrl.widgetCtrl.designerMode();
            // Prevent the switch to true if the required FF are not enabled.
            if(isInDesignerContext && Features.hasFeature(NGI_WIDGETS_IN_DESIGNER_FF_TOKEN) && Features.hasFeature('layout-v2')) {
                vm.isNGICompatible = true;
            }
        }

        /**
         * Initialize the needed projections according to the parameters of the content list.
         */
        function _initProjections() {
            _projection = {
                items: {
                    canContribute: true,
                    id: true,
                    instance: true,
                    privacy: true,
                    properties: true,
                    slug: true,
                    uid: true,
                    renderingType: true
                },
            };

            var properties = vm.widget.properties;

            if (angular.isDefinedAndFilled(properties.fields)) {
                angular.forEach(properties.fields, function forEachFields(field) {
                    if (angular.isUndefined(field) || angular.isUndefinedOrEmpty(field.name) || !field.enable) {
                        return;
                    }

                    var fieldName = field.name.toLowerCase();
                    switch (fieldName) {
                        case 'title':
                            _projection.items.title = true;
                            break;

                        case 'content':
                            _projection.items.description = true;
                            break;

                        case 'follow':
                            _projection.items.subscription = _subscriptionProjection;
                            break;

                        case 'thumbnail':
                            _projection.items.thumbnail = true;
                            break;

                        case 'metadata':
                            _projection.items.metadata = true;
                            break;

                        default:
                            break;
                    }
                });
            } else {
                _projection.items.description = true;
                _projection.items.id = true;
                _projection.items.instance = true;
                _projection.items.slug = true;
                _projection.items.subscription = _subscriptionProjection;
                _projection.items.thumbnail = true;
                _projection.items.title = true;
                _projection.items.uid = true;
                _projection.items.metadata = false;
            }
        }

        /**
         * Fetch metadata for all communities in the list and store in a map
         */
        async function _fetchMetadataForCommunities() {
            const communities = vm.widgetListCtrl.items

            if (isEmpty(communities)) {
                return;
            }

            const metadataIds = uniq(
                flatMap(communities, (community) => [...keys(community.metadata), ...flatten(values(community.metadata))])
            );

            if (metadataIds.length > 0) {
                try {
                    const metadataList = await fetchCommunityMetadata({ metadataIds });

                    vm.metadataMap = createMetadataMap(metadataList);
                } catch (error) {
                    vm.metadataMap = new Map();
                }
            }
        }

        /**
         * Initialize the community list.
         *
         * @param {Object} additionalFilters The additional filters provided by the widget content filter.
         */
        function _initList(additionalFilters) {
            // Skip initial list calls if displayed in NGI
            if(_shouldDisplayAsNGI()) {
                return;
            }

            var properties = vm.widget.properties;
            additionalFilters = additionalFilters || {};

            var displayType = properties.displayType;
            var params = {
                followingOnly: Boolean(properties.followingOnly),
                isCommunityAdmin: Boolean(properties.isCommunityAdmin),
                ids: (displayType === 'pick') ? properties.communityIds : [],
                status: [
                    Config.CONTENT_STATUS.LIVE.value,
                ],
            };

            if (angular.isNumber(properties.maxNumber)) {
                params.maxResults = properties.maxNumber;
            }

            if (angular.isDefinedAndFilled(additionalFilters.query)) {
                params.query = additionalFilters.query;
            }

            if (displayType === 'list') {
                if (angular.isDefinedAndFilled(properties.metadata)) {
                    const metadataByCategory = values(properties.metadata)

                    params.combinedMetadata = map(metadataByCategory, (metadata) => ({ metadata }));
                }

                if (angular.isDefinedAndFilled(additionalFilters.instance)) {
                    params.instanceId = additionalFilters.instance;
                } else if (_.get(properties, 'isAllInstancesSiblings', false)) {
                    params.instanceId = undefined;
                    // Set currentInstanceId property to fetch content from all siblings.
                    params.currentInstanceId = Instance.getCurrentInstanceId();
                } else if (angular.isDefinedAndFilled(properties.instance)) {
                    params.instanceId = properties.instance;
                } else {
                    params.instanceId = [Instance.getCurrentInstanceId()];
                }
            } else {
                params.instanceId = undefined;
            }

            // If we're in 'pick' mode and have not selected anything yet, we want to display no results.
            if (displayType === 'pick' && angular.isUndefinedOrEmpty(properties.communityIds)) {
                angular.extend(params, {
                    queryType: 'getContentByIds',
                });
            }

            _initProjections();

            Community.filterize(params, vm.widgetListCtrl.updateListItems, vm.widgetListCtrl.updateListItems,
                vm.widgetListCtrl.getListKey(), _projection);
        }

        /**
         * Initialize the widget properties and the controller parameters.
         */
        function _initProperties() {
            vm.widget.properties = vm.widget.properties || {};
            var properties = vm.widget.properties;

            if (angular.isUndefinedOrEmpty(properties.viewMode)) {
                properties.viewMode = 'list';
            }

            if (angular.isUndefinedOrEmpty(properties.displayType)) {
                properties.displayType = 'list';
            }

            if (angular.isUndefinedOrEmpty(properties.viewModeVariant)) {
                properties.viewModeVariant = 'group';
            }

            if (angular.isUndefinedOrEmpty(properties.itemsSeparator)) {
                properties.itemsSeparator = true;
            }

            // Fields manager.
            var defaultFields = Config.COMMUNITY_FIELDS;

            var firstFieldsInit = false;

            if (angular.isUndefinedOrEmpty(properties.fields)) {
                properties.fields = [];
                firstFieldsInit = true;
            }

            angular.forEach(defaultFields, function forEachDefaultField(defaultField) {
                if (angular.isString(defaultField)) {
                    defaultField = {
                        // Note: we want thumbnails to be displayed by default because we didnt have a setting for it.
                        enable: (defaultField === 'thumbnail') ? true : firstFieldsInit,
                        name: defaultField,
                    };
                }

                var findInDefaultFields = _.find(properties.fields, {
                    name: defaultField.name,
                });

                if (firstFieldsInit || angular.isUndefinedOrEmpty(findInDefaultFields)) {
                    properties.fields.push(defaultField);
                }
            });

            var instanceProperty = properties.instance || [];
            var instanceSiblings = [];

            Instance.getSiblings().then(function onInstanceSiblingsSuccess(fetchedSiblings) {
                instanceSiblings = fetchedSiblings;

                if ((instanceSiblings.length > 1 && instanceProperty.length > 1) ||
                    properties.isAllInstancesSiblings || properties.displayType === 'pick') {
                    properties.isInstanceNameDisplayed = (angular.isDefined(properties.isInstanceNameDisplayed)) ?
                        properties.isInstanceNameDisplayed : true;
                } else {
                    properties.isInstanceNameDisplayed = false;
                }

                properties.instance = (angular.isDefinedAndFilled(instanceProperty)) ? instanceProperty :
                    [Instance.getCurrentInstanceId()];
            });
        }

        /**
         * Open community picker (only when widget selection type is pick).
         */
        function _openCommunityPicker() {
            if (vm.widget.properties.displayType !== vm.widgetListCtrl.SELECTION_TYPES.pick ||
                Content.getViewMode() !== 'basic' || Content.getAction() === 'get') {
                return;
            }

            Utils.waitForAndExecute('#' + _COMMUNITY_PICKER_ID, function onContentPickerDialogReady() {
                ContentPicker.open(_COMMUNITY_PICKER_ID);
            });
        }
        /////////////////////////////
        //                         //
        //     Public functions    //
        //                         //
        /////////////////////////////

        vm.shouldDisplayAsNGI = _shouldDisplayAsNGI;
        vm.setAsNonNgiCompatible = _setAsNonNgiCompatible;
        vm.setAsNgiCompatible = _setAsNgiCompatible;

        /**
         * Initialize the controller.
         *
         * @param {boolean} saveListKey Indicates if we want to save the list key as the original one.
         */
        vm.init = function init(saveListKey) {
            vm.widget.properties = vm.widget.properties || {};
            var properties = vm.widget.properties;

            properties.communityIds = properties.communityIds || [];

            _COMMUNITY_PICKER_ID += vm.widget.uuid;

            vm.widgetListCtrl.init(saveListKey);

            const isInDesignerContext = vm.widgetListCtrl.widgetCtrl.designerMode();
            // If we are not in the designer context or the content is not v2 compatible
            // we fallback to legacy display (we consider unsaved content as v2 compatible).
            // Here we are trying to guess which display will be the correct one in view mode.
            // This is not perfect, but we want to avoid too much logic in the frontend to know if a content is
            // compatible v2 or not (widget types presents on the content + some specific settings on some widgets)
            if(!isInDesignerContext || (Content.getAction() !== 'create' && Content.getCurrent()?.template.isV2Compatible === false)) {
                _setAsNonNgiCompatible();
            }
        };

        /**
         * Set parent controller.
         *
         * @param {Object} widgetListCtrl The parent controller object.
         */
        this.setParentController = function setParentController(widgetListCtrl) {
            vm.widgetListCtrl = widgetListCtrl;

            vm.widgetListCtrl.widgetListChildCtrl = {
                initList: _initList,
                initProperties: _initProperties,
                onWidgetListClick: _openCommunityPicker,
                shouldDisplayAsNGI: _shouldDisplayAsNGI,
                setAsNgiCompatible: _setAsNgiCompatible,
                setAsNonNgiCompatible: _setAsNonNgiCompatible,
            };

            vm.init(true);
        };

        $scope.$watch('vm.widgetListCtrl.items', _fetchMetadataForCommunities, true)
    }

    /////////////////////////////

    /**
     * The community list widget.
     * Displays a list of communities (either manually picked or the whole list with filters applied).
     *
     * @param {Object} widget The widget configuration object.
     */

    function WidgetCommunityListDirective() {
        'ngInject';

        function WidgetCommunityListLink(scope, el, attrs, ctrls) {
            ctrls[0].setParentController(ctrls[1]);
        }

        return {
            bindToController: true,
            controller: WidgetCommunityListController,
            controllerAs: 'vm',
            link: WidgetCommunityListLink,
            replace: true,
            require: ['widgetCommunityList', '^widgetList'],
            restrict: 'E',
            scope: {
                widget: '<',
            },
            // eslint-disable-next-line max-len
            templateUrl: '/client/front-office/modules/content/modules/widget/modules/widget-community-list/views/widget-community-list.html',
        };
    }

    /////////////////////////////

    angular.module('Widgets').directive('widgetCommunityList', WidgetCommunityListDirective);
})();
